/*
 * Copyright © 2015-2019 Serenova, LLC. All rights reserved.
 */

/**
 *
 * DispatchMappingsForm
 *
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { DetailHeader, InputField, SelectField, DetailsPanelAlert, DetailsPanelMessage } from 'cx-ui-components';
import styled from 'styled-components';

const PanelMessage = styled(DetailsPanelMessage)`
  padding-top: 0;
  & p {
    font-size: 12px;
  }
`;

export default class DispatchMappingsForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      duplicated: false,
      mappingValue: undefined,
      channelType: undefined,
      value: undefined
    };
  }

  onChangeChannelType = (e) => {
    this.setState({ channelType: e?.target.value });
  }

  onChangeMappingValue = (e) => {
    this.setState({ mappingValue: e?.target.value });
  }

  onChangeValue = (e) => {
    this.setState({ value: e?.target.value });
  }

  isDuplicated = () => {
    const dispatchDuplicated = this.props.duplicatedDispatchMappingsList;

    if (dispatchDuplicated && dispatchDuplicated.length > 0) {
      this.setState({ duplicated: true });
    } else {
      this.setState({ duplicated: false });
    }
  }

  componentDidMount() {
    this.setState({
      channelType: this.props.initialValues.get('channelType'),
      mappingValue: this.props.initialValues.get('interactionField'),
      value: this.props.initialValues.get('value')
    });
    this.isDuplicated();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevState.channelType !== this.state.channelType ||
      prevState.mappingValue !== this.state.mappingValue ||
      prevState.value !== this.state.value) {
      this.isDuplicated();
    }
  }

  render() {
    return (
      <form onSubmit={this.props.handleSubmit} key={this.props.key}>
        {this.props.inherited && <DetailsPanelAlert text="This Dispatch Mapping is inherited and cannot be edited"/>}
        <DetailHeader text="Details"/>
        <InputField
          name="name"
          label="Name *"
          componentType="input"
          inputType="text"
          data-automation="nameInput"
          disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission}
        />
        <InputField
          name="description"
          label="Description"
          componentType="textarea"
          inputType="text"
          data-automation="descriptionInput"
          disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission}
        />
        <SelectField
          name="channelType"
          label="Interaction Type *"
          data-automation="interactionList"
          required
          options={[
            { value: 'any', label: 'Any' },
            { value: 'voice', label: 'Voice' },
            { value: 'sms', label: 'Sms' },
            { value: 'email', label: 'Email' },
            { value: 'messaging', label: 'Messaging' },
            { value: 'work-item', label: 'Work Item' }
          ]}
          onChange={(e) => this.onChangeChannelType(e)}
          disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission}
        />
        <SelectField
          name="interactionField"
          label="Mapping *"
          data-automation="mappingList"
          required
          options={[
            { value: 'customer', label: 'Customer' },
            { value: 'contact-point', label: 'Contact Point' },
            { value: 'source', label: 'Integration' },
            { value: 'direction', label: 'Direction' }
          ]}
          onChange={(e) => this.onChangeMappingValue(e)}
          disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission}
        />
        {this.props.mappingValue === 'source' && (
          <SelectField
            name="value"
            data-automation="integrationList"
            required
            label="Integration *"
            options={this.props.integrationElements}
            onChange={(e) => this.onChangeValue(e)}
            disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission}
          />
        )}

        {this.props.mappingValue === 'direction' && (
          <SelectField
            name="value"
            data-automation="directionList"
            label="Direction *"
            required
            options={[
              { value: 'inbound', label: 'Inbound' },
              { value: 'outbound', label: 'Outbound' },
              { value: 'agent-initiated', label: 'Agent Initiated' }
            ]}
            onChange={(e) => this.onChangeValue(e)}
            disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission}
          />
        )}

        {(this.props.mappingValue === 'customer' || this.props.mappingValue === 'contact-point') && (
          <Fragment>
            <InputField
              name="value"
              label="Mapping Value *"
              data-automation="phoneValueInput"
              componentType="input"
              inputType="text"
              className="frm-dispatch-mappings-name"
              onChange={(e) => this.onChangeValue(e)}
              disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission}
              placeholder={this.props.mappingValueMessage}
            />
            {this.props.isMappingValueInvalid && (
              <PanelMessage text={`Warning: Not a valid E.164 formatted phone number or SIP address.`} type="warning"/>
            )}
          </Fragment>
        )}
        <SelectField
          name="flowId"
          label="Dispatch to flow *"
          required={this.props.initialValues.get('id') !== undefined}
          data-automation="flowList"
          options={this.props.flowIds && this.props.flowIds.size > 0 ? this.props.flowIds : undefined}
          disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission}
        />
        {this.props.flowId && (
          <SelectField
            name="version"
            label="Flow Version *"
            required={this.props.initialValues.get('id') !== undefined}
            data-automation="flowVersionList"
            options={!this.props.flowsFetching ? this.props.flowVersions : undefined}
            disabled={this.props.isSaving || this.props.inherited || !this.props.userHasUpdatePermission || this.props.flowsFetching || !this.props.flowId}
          />
        )}
        {this.state.duplicated &&
          <DetailsPanelMessage
            name="duplicated"
            text={`There is a dispatch mapping already registered with the same value and the same channel type in this tenant.`}
            type="warning"
          />
        }
      </form>
    );
  }

}

DispatchMappingsForm.propTypes = {
  channelType: PropTypes.string,
  dispatchMappingsList: PropTypes.object,
  duplicatedDispatchMappingsList: PropTypes.array,
  flowsFetching: PropTypes.bool,
  flowId: PropTypes.string,
  flowIds: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string.isRequired
      })
    ),
    PropTypes.object
  ]),
  flowVersions: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string.isRequired
      })
    ),
    PropTypes.object
  ]),
  handleSubmit: PropTypes.func.isRequired,
  inherited: PropTypes.bool,
  initialValues: PropTypes.object,
  integrationElements: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string.isRequired
    })
  ),
  isMappingValueInvalid: PropTypes.bool,
  isSaving: PropTypes.bool,
  key: PropTypes.string,
  mappingValue: PropTypes.string,
  mappingValueMessage: PropTypes.string,
  userHasUpdatePermission: PropTypes.bool,
  value: PropTypes.string
};
